<template>
  <div class="radar-chart">
    <div v-if="title" class="radar-chart__head">
      <span class="radar-chart__dot" aria-hidden="true"></span>
      <h3 class="radar-chart__title">{{ title }}</h3>
    </div>
    <div class="radar-chart__canvas-wrap">
      <canvas
        ref="radarCanvas"
        class="radar-chart__canvas"
        role="img"
        :aria-label="radarAriaLabel"
      ></canvas>
    </div>
  </div>
</template>

<script>
import { markRaw, toRaw } from "vue";
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
} from "chart.js";

Chart.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend, Title);

/**
 * Императивный Chart.js без vue-chartjs.
 *
 * Важно: Chart-инстанс и options НЕ должны попадать в реактивную систему Vue.
 * Иначе Vue оборачивает их Proxy, мутации Chart.js внутри update() триггерят реактивные
 * эффекты, и страница уходит в петлю (визуально — «бесконечная загрузка» / RAM).
 *
 * Поэтому options — заморожены и хранятся как module-level константа,
 * а сам Chart создаётся через markRaw().
 */
function deepFreeze(o) {
  if (o == null || typeof o !== "object") return o;
  Object.values(o).forEach(deepFreeze);
  return Object.freeze(o);
}

const RADAR_CHART_OPTIONS = deepFreeze({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  layout: {
    padding: { top: 18, bottom: 14, left: 22, right: 22 },
  },
  scales: {
    r: {
      beginAtZero: true,
      min: 0,
      suggestedMin: 0,
      suggestedMax: 5,
      angleLines: {
        color: "rgba(15, 23, 42, 0.08)",
      },
      grid: {
        color: "rgba(15, 23, 42, 0.08)",
        circular: false,
      },
      ticks: {
        stepSize: 1,
        backdropColor: "transparent",
        color: "rgba(71, 85, 105, 0.7)",
        font: {
          size: 10,
        },
        showLabelBackdrop: false,
      },
      pointLabels: {
        font: {
          size: 11,
          weight: "600",
        },
        color: "rgba(15, 23, 42, 0.78)",
        padding: 10,
        centerPointLabels: true,
      },
    },
  },
  plugins: {
    legend: {
      position: "bottom",
      display: (ctx) => (ctx?.chart?.data?.datasets?.length || 0) > 1,
      labels: {
        usePointStyle: true,
        pointStyle: "circle",
        boxWidth: 8,
        boxHeight: 8,
        padding: 14,
        color: "rgba(15, 23, 42, 0.78)",
        font: {
          size: 12,
          weight: "600",
        },
      },
    },
    title: {
      display: false,
    },
    tooltip: {
      backgroundColor: "rgba(15, 23, 42, 0.92)",
      titleColor: "#fff",
      bodyColor: "#e2e8f0",
      titleFont: { size: 12, weight: "600" },
      bodyFont: { size: 12 },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
    },
  },
  elements: {
    line: {
      borderJoinStyle: "round",
      borderCapStyle: "round",
      tension: 0.18,
    },
    point: {
      hoverRadius: 5,
      hitRadius: 8,
      radius: 3,
    },
  },
});

const RADAR_PALETTE = [
  { fill: "rgba(91, 108, 255, 0.18)", line: "rgba(91, 108, 255, 1)" },
  { fill: "rgba(244, 162, 97, 0.22)", line: "rgba(244, 162, 97, 1)" },
];

function styleDataset(ds, index) {
  const palette = RADAR_PALETTE[index] || RADAR_PALETTE[0];
  const line = ds.borderColor || palette.line;
  const fill = ds.backgroundColor || palette.fill;
  return {
    ...ds,
    backgroundColor: fill,
    borderColor: line,
    borderWidth: ds.borderWidth || 2,
    pointBackgroundColor: line,
    pointBorderColor: "#fff",
    pointBorderWidth: 1,
    pointRadius: 3,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: "#fff",
    pointHoverBorderColor: line,
    pointHoverBorderWidth: 2,
  };
}

function deepPlainClone(obj) {
  if (obj == null) return obj;
  return JSON.parse(JSON.stringify(obj));
}

/** Перенос по словам для подписей осей радара (Chart.js: string | string[]). */
function wrapRadarLabel(text, maxChars) {
  if (text == null || text === "") return text;
  const s = String(text).trim();
  if (s.length <= maxChars) return s;
  const words = s.split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const candidate = line ? `${line} ${w}` : w;
    if (candidate.length <= maxChars) {
      line = candidate;
      continue;
    }
    if (line) lines.push(line);
    if (w.length <= maxChars) {
      line = w;
      continue;
    }
    let rest = w;
    while (rest.length > maxChars) {
      lines.push(rest.slice(0, maxChars));
      rest = rest.slice(maxChars);
    }
    line = rest;
  }
  if (line) lines.push(line);
  return lines.length === 1 ? lines[0] : lines;
}

function cloneChartDataForJs(d) {
  if (!d) return d;
  const raw = toRaw(d);
  const labels = Array.isArray(raw.labels) ? raw.labels.map((l) => l) : raw.labels;
  const datasets = (raw.datasets || []).map((ds, i) => {
    const r = toRaw(ds);
    const dataArr = Array.isArray(r.data) ? r.data.map((v) => Number(v)) : r.data;
    return styleDataset({ ...r, data: dataArr }, i);
  });
  return { labels, datasets };
}

export default {
  name: "RadarChart",
  props: {
    chartData: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: "",
    },
    labelMaxChars: {
      type: Number,
      default: 16,
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  computed: {
    radarAriaLabel() {
      return this.title || "radar";
    },
    normalizedChartData() {
      const d = this.chartData;
      if (!d) return null;
      if (!Array.isArray(d.labels)) {
        return cloneChartDataForJs(d);
      }
      const raw = toRaw(d);
      const maxChars = Math.max(8, Number(this.labelMaxChars) || 16);
      const labels = raw.labels.map((l) => {
        if (Array.isArray(l)) {
          const flat = l.flat().map(String).join(" ").trim();
          return wrapRadarLabel(flat, maxChars);
        }
        return wrapRadarLabel(l, maxChars);
      });
      return cloneChartDataForJs({
        labels,
        datasets: raw.datasets,
      });
    },
  },
  watch: {
    normalizedChartData: {
      handler(val) {
        this.$nextTick(() => this.syncChart(val));
      },
      deep: false,
    },
  },
  mounted() {
    this.$nextTick(() => this.syncChart(this.normalizedChartData));
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    destroyChart() {
      if (this.chart) {
        toRaw(this.chart).destroy();
        this.chart = null;
      }
    },
    syncChart(val) {
      const el = this.$refs.radarCanvas;
      if (!el) return;
      if (!val || !Array.isArray(val.labels) || !val.datasets) {
        this.destroyChart();
        return;
      }
      const data = deepPlainClone(val);
      if (!this.chart) {
        const instance = new Chart(el, {
          type: "radar",
          data,
          options: deepPlainClone(RADAR_CHART_OPTIONS),
        });
        this.chart = markRaw(instance);
        return;
      }
      const raw = toRaw(this.chart);
      raw.data = data;
      raw.update("none");
    },
  },
};
</script>

<style scoped>
.radar-chart {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  box-sizing: border-box;
  border-radius: 18px;
  border: 1px solid rgba(10, 20, 45, 0.08);
  background: linear-gradient(175deg, rgba(255, 255, 255, 0.96), rgba(246, 249, 255, 0.9));
  box-shadow: 0 18px 56px rgba(10, 20, 45, 0.09);
  padding: 16px 16px 18px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.radar-chart:hover {
  transform: translateY(-2px);
  box-shadow: 0 22px 64px rgba(10, 20, 45, 0.12);
}

.radar-chart__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  padding: 0 4px;
}

.radar-chart__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5b6cff 0%, #7c83ff 100%);
  box-shadow: 0 0 0 3px rgba(91, 108, 255, 0.14);
  flex: 0 0 auto;
}

.radar-chart__title {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  letter-spacing: -0.1px;
  color: rgba(10, 20, 45, 0.92);
  line-height: 1.25;
}

/* Chart.js requires the canvas to live inside a relatively-positioned,
   dedicated container so the ResizeObserver can read real width/height.
   Without this the radar collapses to legend only. */
.radar-chart__canvas-wrap {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-height: 300px;
}

.radar-chart__canvas {
  display: block;
}

@media (max-width: 600px) {
  .radar-chart {
    padding: 14px 12px 16px;
  }

  .radar-chart__canvas-wrap {
    min-height: 280px;
  }
}
</style>
